import React, { type ComponentPropsWithoutRef } from 'react'

import styles from '@runtime/components/ui/Modal.module.scss'

const DEFAULT_ANIM_MS = 180

type OverlayProps = ComponentPropsWithoutRef<'div'> & {
  lite?: boolean
  closing?: boolean
}

export function Overlay({ lite, closing, className, ...props }: OverlayProps) {
  const classes = [
    'overlay',
    styles.overlay,
    lite ? 'lite' : '',
    lite ? styles.lite : '',
    closing ? 'closing' : '',
    closing ? styles.closing : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ')
  return <div className={classes} {...props} />
}

type ModalContentProps = ComponentPropsWithoutRef<'div'> & {
  base?: boolean
}

export function ModalContent({
  base = true,
  className,
  ...props
}: ModalContentProps) {
  const classes = [
    base ? 'content' : '',
    base ? styles.content : '',
    styles.contentAnim,
    className || '',
  ]
    .filter(Boolean)
    .join(' ')
  return <div className={classes} {...props} />
}

type ModalProps = {
  open: boolean
  lite?: boolean
  overlayClassName?: string
  contentClassName?: string
  contentStyle?: React.CSSProperties
  contentProps?: ComponentPropsWithoutRef<'div'> & {
    'data-testid'?: string
  }
  closeOnOverlayClick?: boolean
  onClose?: () => void
  useBaseContentClass?: boolean
  durationMs?: number
  children: React.ReactNode
}

export function Modal({
  open,
  lite,
  overlayClassName,
  contentClassName,
  contentStyle,
  contentProps,
  closeOnOverlayClick = true,
  onClose,
  useBaseContentClass = true,
  durationMs = DEFAULT_ANIM_MS,
  children,
}: ModalProps) {
  const [present, setPresent] = React.useState(open)
  const [closing, setClosing] = React.useState(false)

  const lastChildrenRef = React.useRef<React.ReactNode>(children)
  if (open) lastChildrenRef.current = children

  React.useEffect(() => {
    if (open) {
      setPresent(true)
      setClosing(false)
      return
    }

    if (!present) return

    setClosing(true)
    const t = window.setTimeout(() => {
      setPresent(false)
      setClosing(false)
    }, durationMs)

    return () => window.clearTimeout(t)
  }, [open, present, durationMs])

  if (!present) return null

  const {
    className: contentPropsClassName,
    style: contentPropsStyle,
    onClick: onContentClick,
    ...restContentProps
  } = contentProps || {}
  const contentClass = [contentClassName || '', contentPropsClassName || '']
    .filter(Boolean)
    .join(' ')
  const mergedContentStyle = { ...contentStyle, ...contentPropsStyle }

  return (
    <Overlay
      lite={lite}
      closing={closing}
      className={overlayClassName}
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!closeOnOverlayClick) return
        if (closing) return
        onClose && onClose()
      }}
    >
      <ModalContent
        base={useBaseContentClass}
        className={contentClass}
        style={mergedContentStyle}
        onClick={(e) => {
          e.stopPropagation()
          onContentClick && onContentClick(e)
        }}
        {...restContentProps}
      >
        {open ? children : lastChildrenRef.current}
      </ModalContent>
    </Overlay>
  )
}

type ModalTitleProps = ComponentPropsWithoutRef<'div'>

export function ModalTitle({ className, ...props }: ModalTitleProps) {
  const classes = ['h', styles.title, className || ''].filter(Boolean).join(' ')
  return <div className={classes} {...props} />
}
